import { ActionIcon, Modal } from '@mantine/core'
import { Icon } from './Icon'
import { Colors } from '../style/colors'
import { GlobalModalContext, ModalContent, ModalHandler } from '../hooks/useGlobalModal'
import { FC, FunctionComponent, ReactNode, useMemo, useState } from 'react'

type AdditionalProps = object

const CLOSE_TIMEOUT_MS = 250
const modalStyles = {
  inner: {
    paddingInline: 0,
    paddingTop: '5dvh',
    paddingBottom: '25dvh',
  },
  content: {
    border: 'none',
    outline: 'none',
  },
  body: {
    display: 'grid' as const,
    rowGap: '18px',
    padding: '18px 24px 24px',
    gridTemplateAreas: `
      'title close'
      'body body'
      'actions actions'
    `,
    gridTemplateColumns: '1fr min-content',
    gridTemplateRows: 'min-content 1fr min-content',
  },
}
const NullComponent: FunctionComponent = () => null
const defaultContent: ModalContent = { Component: NullComponent, additionalProps: {} }

export const GlobalModalWrapper: FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setOpen] = useState(false)
  const [content, setContent] = useState<ModalContent>(defaultContent)

  const handler = useMemo((): ModalHandler => {
    return {
      open: (comp, additionalProps, containerProps) => {
        setContent({
          Component: comp as unknown as FunctionComponent<AdditionalProps>,
          additionalProps,
          containerProps,
        })
        setOpen(true)
      },
      close: async () => {
        setOpen(false)
        return new Promise((resolve) =>
          setTimeout(() => {
            setContent(defaultContent)
            resolve()
          }, CLOSE_TIMEOUT_MS + 1)
        )
      },
    }
  }, [])

  const { Component, additionalProps, containerProps } = content

  const onClose = async () => {
    containerProps?.onBeforeDefaultClose?.()
    await handler.close()
    containerProps?.onAfterDefaultClose?.()
  }

  return (
    <GlobalModalContext.Provider value={handler}>
      <Modal
        opened={isOpen}
        onClose={onClose}
        zIndex={1000}
        withCloseButton={false}
        closeOnClickOutside={containerProps?.shouldCloseOnOverlayClick ?? true}
        closeOnEscape={containerProps?.shouldCloseOnEsc ?? true}
        centered
        size="auto"
        overlayProps={{ backgroundOpacity: 0.4, color: Colors.Jet }}
        styles={modalStyles}
      >
        {(containerProps?.showCloseButton ?? true) && (
          <ActionIcon onClick={onClose} variant="subtle" color={Colors.King} radius="xl" size="lg" style={{ gridArea: 'close' }}>
            <Icon name="close" size="lg" />
          </ActionIcon>
        )}
        <Component {...additionalProps} />
      </Modal>
      {children}
    </GlobalModalContext.Provider>
  )
}
